import {
  connectToCardIframe,
  type IFrameConnection,
  type CardInputType as PenpalCardInputType,
  type CheckoutResponse as PenpalCheckoutResponse,
} from "../penpal/connect-card";

// Re-export types for public API
export type CardInputType = PenpalCardInputType;
export type CheckoutResponse = PenpalCheckoutResponse;

import { AnalyticsService } from "../analytics";
import { TunnelXManager } from "../penpal/connect-tunnel-x";
import type { PaymentKitErrors, PaymentKitFields, PaymentKitStates, TInternalFuncs } from "../types";
import {
  $,
  collectFraudMetadata,
  createCheckoutIFrame,
  definePaymentMethod,
  validateFormFields,
  withRequestId,
} from "../utils";
import { handleNextAction } from "./next-action-handlers";
import { createVgsCardFields, initVgsCollect, loadVgsCollectScript } from "./vgs-collect-loader";

/**
 * Splits a full name into first name and last name.
 * First word becomes firstName, rest becomes lastName.
 */
const splitName = (fullName: string): { firstName: string; lastName?: string } => {
  const trimmed = fullName.trim();
  const spaceIndex = trimmed.indexOf(" ");
  if (spaceIndex === -1) {
    return { firstName: trimmed };
  }
  return {
    firstName: trimmed.substring(0, spaceIndex),
    lastName: trimmed.substring(spaceIndex + 1).trim() || undefined,
  };
};

/**
 * Maps PaymentKitFields to the customerInfo structure expected by the API.
 */
const mapFieldsToCustomerInfo = (fields: PaymentKitFields) => {
  const { firstName, lastName } = splitName(fields.customer_name);

  const hasBillingAddress =
    fields.customer_country ||
    fields.customer_zip_code ||
    fields.customer_address_line1 ||
    fields.customer_address_line2 ||
    fields.customer_city ||
    fields.customer_state;

  const hasShippingAddress =
    fields.shipping_address_line1 ||
    fields.shipping_address_line2 ||
    fields.shipping_city ||
    fields.shipping_state ||
    fields.shipping_zip_code ||
    fields.shipping_country;

  let taxIds: string[] | undefined;
  if (fields.customer_tax_ids) {
    try {
      taxIds = JSON.parse(fields.customer_tax_ids);
    } catch {
      taxIds = undefined;
    }
  }

  return {
    email: fields.customer_email || undefined,
    firstName,
    lastName,
    businessName: fields.customer_business_name || undefined,
    taxIds: taxIds?.length ? taxIds : undefined,
    billingAddress: hasBillingAddress
      ? {
          country: fields.customer_country || undefined,
          zipCode: fields.customer_zip_code || undefined,
          line1: fields.customer_address_line1 || undefined,
          line2: fields.customer_address_line2 || undefined,
          city: fields.customer_city || undefined,
          state: fields.customer_state || undefined,
        }
      : undefined,
    shippingAddress: hasShippingAddress
      ? {
          line1: fields.shipping_address_line1 || undefined,
          line2: fields.shipping_address_line2 || undefined,
          city: fields.shipping_city || undefined,
          state: fields.shipping_state || undefined,
          zipCode: fields.shipping_zip_code || undefined,
          country: fields.shipping_country || undefined,
        }
      : undefined,
  };
};

type CardStates = PaymentKitStates & {
  cardSetupIntentId?: string;
  cardInputConnections: Partial<Record<PenpalCardInputType, IFrameConnection>>;
  vgsForm?: VGSCollectForm;
  vgsFieldsCleanup?: () => void;
  vgsFocusCallbacks?: Partial<Record<PenpalCardInputType, (isFocused: boolean) => void>>;
  vgsMountSelectors?: Partial<Record<PenpalCardInputType, string>>;
  vgsPlaceholders?: Partial<Record<PenpalCardInputType, string>>;
};

type CreateElementOptions = Partial<Parameters<typeof connectToCardIframe>[1]> & {
  style?: Record<string, string>;
  placeholder?: string;
};

const defCreateElement = (states: CardStates) => {
  const { baseUrl, apiBaseUrl, cardInputConnections, secureToken, timingTracker } = states;

  return (type: PenpalCardInputType, options: CreateElementOptions) => {
    const { style, onLoaded, onFocusChange, placeholder } = options;

    const mountIFrame = (parentSelector: string) => {
      // Mutable cleanup callbacks — set once mode is determined and mount completes
      let cleanupVgs: (() => void) | undefined;
      let cleanupDirect: (() => void) | undefined;
      // Cancellation flag: prevents deferred doMount from executing after unmount.
      // Needed because React strict mode may call unmount before the deferred
      // _sessionConfigReady promise resolves.
      let cancelled = false;

      /**
       * Performs the actual DOM mount after the tokenization mode is known.
       * Runs synchronously when mode is explicitly provided, or after the
       * auto-detect fetch resolves.
       */
      // Store mount selector so VGS init (triggered by card_pan) can read
      // the exp/cvc selectors that were registered by their own createElement calls.
      if (!states.vgsMountSelectors) states.vgsMountSelectors = {};
      states.vgsMountSelectors[type] = parentSelector;

      const doMount = () => {
        if (cancelled) return;
        const isVgsMode = states.cardTokenizationMode === "vgs";
        console.log(
          `[PaymentKit] card.doMount(${type}) — mode: ${states.cardTokenizationMode}, vgsVaultId: ${states.vgsVaultId ?? "unset"}, vgsEnvironment: ${states.vgsEnvironment ?? "unset"}`,
        );

        // VGS mode: initialize VGS Collect form and mount fields on card_pan
        if (isVgsMode && states.vgsVaultId && states.vgsEnvironment) {
          // Store per-field focus callbacks and placeholders so createVgsCardFields
          // can wire each field to its own callback/placeholder. All three createElement
          // calls complete before the CDN script loads, so all values are available by then.
          if (onFocusChange) {
            if (!states.vgsFocusCallbacks) states.vgsFocusCallbacks = {};
            states.vgsFocusCallbacks[type] = onFocusChange;
          }
          if (placeholder) {
            if (!states.vgsPlaceholders) states.vgsPlaceholders = {};
            states.vgsPlaceholders[type] = placeholder;
          }

          if (type === "card_pan") {
            const vaultId = states.vgsVaultId;
            const vgsEnv = states.vgsEnvironment;

            console.log(`[PaymentKit] VGS mode active — loading VGS Collect CDN for vault ${vaultId} (${vgsEnv})...`);
            const VGS_CDN_TIMEOUT_MS = 15_000;
            let cdnTimeoutId: ReturnType<typeof setTimeout>;
            Promise.race([
              loadVgsCollectScript(),
              new Promise<never>((_, reject) => {
                cdnTimeoutId = setTimeout(() => {
                  console.warn(`[PaymentKit] VGS Collect CDN load timed out after ${VGS_CDN_TIMEOUT_MS / 1000}s`);
                  reject(new Error("VGS Collect CDN load timed out"));
                }, VGS_CDN_TIMEOUT_MS);
              }),
            ])
              .finally(() => clearTimeout(cdnTimeoutId))
              .then(() => {
                if (cancelled) return;
                console.log("[PaymentKit] VGS Collect CDN loaded — initializing form...");
                const form = initVgsCollect(vaultId, vgsEnv);
                if (cancelled) {
                  form.unmount();
                  return;
                }
                states.vgsForm = form;

                const css = style
                  ? Object.fromEntries(
                      Object.entries(style).map(([k, v]) => [k.replace(/([A-Z])/g, "-$1").toLowerCase(), v]),
                    )
                  : undefined;

                // Read exp/cvc selectors from shared state.
                // Each createElement("card_exp/card_cvc").mount(selector) stores its
                // selector before this async callback runs, so they're available here.
                const expSelector = states.vgsMountSelectors?.card_exp;
                const cvcSelector = states.vgsMountSelectors?.card_cvc;
                if (!expSelector || !cvcSelector) {
                  const msg =
                    `[PaymentKit] VGS mode requires all three card fields to be mounted. ` +
                    `Missing: ${!expSelector ? "card_exp" : ""}${!expSelector && !cvcSelector ? ", " : ""}${!cvcSelector ? "card_cvc" : ""}`;
                  console.error(msg);
                  onLoaded?.();
                  return;
                }
                console.log(
                  `[PaymentKit] Creating VGS card fields — pan: ${parentSelector}, exp: ${expSelector}, cvc: ${cvcSelector}`,
                );
                states.vgsFieldsCleanup = createVgsCardFields(
                  form,
                  {
                    pan: parentSelector,
                    exp: expSelector,
                    cvc: cvcSelector,
                  },
                  css,
                  states.vgsFocusCallbacks,
                  states.vgsPlaceholders,
                );

                console.log("[PaymentKit] VGS card fields created successfully");
                timingTracker.trackInputReady();
                onLoaded?.();
              })
              .catch((err) => {
                console.error("[PaymentKit] Failed to load VGS Collect script. Card input will not be available:", err);
                // Signal loaded so the form exits loading state rather than hanging forever.
                // Card fields won't render, but submit will fail with a clear error.
                onLoaded?.();
              });
          }

          // For non-pan fields in VGS mode, signal loaded immediately since
          // VGS handles all three fields from the card_pan mount.
          if (type !== "card_pan") {
            onLoaded?.();
          }

          cleanupVgs = () => {
            if (type === "card_pan" && states.vgsForm) {
              try {
                states.vgsFieldsCleanup?.();
              } catch {
                // VGS may try to remove iframes from containers already removed by React
              }
              states.vgsFieldsCleanup = undefined;
              try {
                states.vgsForm.unmount();
              } catch {
                // VGS unmount may fail if DOM nodes are already detached
              }
              states.vgsForm = undefined;
            }
          };
          return;
        }

        // If we got here and mode was "vgs", it means VGS config was incomplete
        if (isVgsMode) {
          console.warn(
            `[PaymentKit] VGS mode requested but config incomplete (vgsVaultId: ${states.vgsVaultId ?? "unset"}, vgsEnvironment: ${states.vgsEnvironment ?? "unset"}) — using direct mode`,
          );
        }

        // Direct mode: existing penpal iframe logic
        const parent = $(parentSelector) as HTMLElement;

        // Read container padding so the embed input matches the merchant's
        // visual intent. The iframe uses absolute positioning to fill the
        // full container, so the input needs its own padding for alignment.
        const computed = getComputedStyle(parent);
        const containerPadding = computed.padding !== "0px" ? computed.padding : undefined;
        const enhancedStyle = {
          ...(style ?? {}),
          height: "100%",
          boxSizing: "border-box",
          ...(containerPadding ? { padding: containerPadding } : {}),
        };

        const params: Record<string, string> = {
          checkout_token: secureToken,
          api_base_url: apiBaseUrl,
          style: JSON.stringify(enhancedStyle),
        };

        const iframe = createCheckoutIFrame(type.replace("_", "-"), baseUrl, params);

        // Use absolute positioning so the iframe covers the full container
        // including any padding the merchant's CSS applies.
        if (computed.position === "static") {
          parent.style.position = "relative";
        }
        iframe.style.position = "absolute";
        iframe.style.inset = "0";

        // Set cleanup before DOM mutation to avoid leaking the iframe if
        // unmount() is called between appendChild and onload.
        cleanupDirect = () => {
          const connection = cardInputConnections[type];
          connection?.destroy();
          iframe.remove();
          states.cardInputConnections[type] = undefined;
        };

        parent.appendChild(iframe);

        iframe.onload = () => {
          if (type === "card_pan") {
            timingTracker.trackInputReady();
          }

          const connection = connectToCardIframe(iframe, {
            onLoaded: onLoaded || (() => {}),
            onFocusChange: onFocusChange || (() => {}),
          });

          states.cardInputConnections[type] = connection;
        };
      };

      // If session config needs auto-detection, wait for it; otherwise mount synchronously
      if (states._sessionConfigReady) {
        states._sessionConfigReady.then(doMount);
      } else {
        doMount();
      }

      return {
        unmount: () => {
          cancelled = true;
          cleanupVgs?.();
          cleanupDirect?.();
        },
      };
    };

    return { mount: mountIFrame };
  };
};

/**
 * Terminal failure states for a card checkout. Mirrors the backend checkout-attempt state machine
 * (app/modules/checkout/checkout_attempt/checkout_attempt_state_machine.py — the only terminal
 * success state is `checkout_succeeded`). A checkout in one of these states must be surfaced to the
 * merchant as an error so `onError` — not `onSuccess` — fires.
 *
 * Note: 3DS authentication *passing* does NOT imply the charge succeeded — a pass-then-decline
 * concludes with `payment_failed` and no further `nextAction`. Other non-terminal states (e.g.
 * `payment_started` from an async "pending" charge that carries no `nextAction`) are intentionally
 * left to the success/data path here, preserving existing behavior for the non-3DS flow.
 */
const CHECKOUT_FAILURE_STATES = new Set(["payment_failed", "checkout_failed"]);

const isCheckoutFailure = (result: CheckoutResponse): boolean => CHECKOUT_FAILURE_STATES.has(result.state);

const defSubmitPayment = (states: CardStates) => {
  const submitPayment: TInternalFuncs["submitPayment"] = async (fields, options) => {
    // Ensure session config is loaded (no-op if explicitly provided)
    if (states._sessionConfigReady) await states._sessionConfigReady;
    const isVgsMode = states.cardTokenizationMode === "vgs";

    const { timingTracker, checkoutRequestId } = states;

    // Track submit event
    timingTracker.trackSubmit();

    // Use the checkout request ID from states (generated on page load)
    const requestOptions = withRequestId(checkoutRequestId);

    const tunnelX = await TunnelXManager.createFromPenpalConnection(states.tunnelXConnection);

    // Step 1. Validate form values.
    // Form field validation is shared across both modes.
    // In VGS mode, card field validation is handled by VGS Collect (built-in validators).
    // In direct mode, card field validation is handled by penpal iframes.
    const skipCustomerValidation = options?.skipCustomerValidation === true;
    const validateFormResult = skipCustomerValidation
      ? { isSuccess: true as const, errors: {} as PaymentKitErrors }
      : await validateFormFields(fields);
    if (!isVgsMode) {
      const validateCardResult = await validateCardFields(states);
      if (!(validateCardResult.isSuccess && validateFormResult.isSuccess)) {
        timingTracker.trackFail(null, "validation_error", "Form validation failed");
        return { errors: { ...validateCardResult.errors, ...validateFormResult.errors } };
      }
    } else if (!validateFormResult.isSuccess) {
      timingTracker.trackFail(null, "validation_error", "Form validation failed");
      return { errors: validateFormResult.errors };
    }

    // Step 2. Create card setup intent if not present
    if (!states.cardSetupIntentId) {
      const res = await tunnelX.publicEndpoints.createCardSetupIntent({
        checkoutToken: states.secureToken,
      });
      states.cardSetupIntentId = res.cardSetupIntentId;
    }

    // Step 3. Submit card values.
    if (isVgsMode) {
      // VGS mode: create card via CMP, then send aliases to backend
      console.log("[PaymentKit] Submitting card via VGS CMP...");
      const vgsSubmitResult = await submitVgsCardFields(states, tunnelX);
      if (!vgsSubmitResult.isSuccess) {
        // Include field-level error details for PostHog visibility
        const errorDetail = Object.entries(vgsSubmitResult.errors)
          .map(([k, v]) => `${k}: ${v}`)
          .join(", ");
        timingTracker.trackFail(null, "card_submit_error", errorDetail || "VGS card submission failed");
        return { errors: vgsSubmitResult.errors };
      }
    } else {
      // Direct mode: submit via penpal iframes
      const submitCardResult = await submitCardFields(states);
      if (!submitCardResult.isSuccess) {
        timingTracker.trackFail(null, "card_submit_error", "Card submission failed");
        return { errors: submitCardResult.errors };
      }
    }

    // Step 4. Get card setup intent.
    const cardSetupIntent = await tunnelX.publicEndpoints.getCardSetupIntent({
      cardSetupIntentId: states.cardSetupIntentId,
      checkoutToken: states.secureToken,
    });
    if (!cardSetupIntent.isCardAllSet) {
      const errors = {} as PaymentKitErrors;
      if (!cardSetupIntent.isCardPanSet) errors.card_pan = "required";
      if (!cardSetupIntent.isCardExpSet) errors.card_exp = "required";
      if (!cardSetupIntent.isCardCvcSet) errors.card_cvc = "required";
      timingTracker.trackFail(null, "card_setup_incomplete", "Card details incomplete");
      return { errors };
    }

    console.log("Card setup intent is set ✅", cardSetupIntent);
    console.log("Fields", fields);

    // Step 5. Submit card checkout with customer info and fraud metadata
    // Include processor_fraud_info with airwallex_device_id for fraud fingerprinting
    const fraudMetadata = {
      ...collectFraudMetadata(),
      processorFraudInfo: {
        airwallexDeviceId: states.checkoutRequestId,
      },
    };
    let currentResult = await tunnelX.publicEndpoints.cardCheckout(
      {
        checkoutToken: states.secureToken,
        publicCardCheckoutRequest: {
          cardSetupIntentId: states.cardSetupIntentId,
          customerInfo: mapFieldsToCustomerInfo(fields),
          fraudMetadata,
        },
      },
      requestOptions,
    );

    console.log("Card checkout result:", currentResult);

    // Step 6. Handle next actions in a loop (supports multiple 3DS challenges)
    // This loop handles the case where one processor fails and we try another
    // that also requires 3DS authentication.
    const MAX_USER_ACTIONS = 5; // Safety limit to prevent infinite loops
    let userActionCount = 0;

    while (currentResult.nextAction && userActionCount < MAX_USER_ACTIONS) {
      userActionCount++;
      console.log(`Handling user action ${userActionCount}/${MAX_USER_ACTIONS}...`);

      const actionResult = await handleNextAction(currentResult.nextAction);

      // Always call verify endpoint so backend can properly conclude the checkout.
      // This is needed even on 3DS failure so the backend can transition from
      // user_actions_requested to card_payment_concluded with proper failure state.
      console.log("User action completed, verifying checkout...");
      const verifyResult = await tunnelX.publicEndpoints.cardCheckoutVerify(
        {
          checkoutToken: states.secureToken,
        },
        requestOptions,
      );

      // Check if another action is required (e.g., cascade to next processor with 3DS)
      // This must be checked BEFORE returning error, as cascade may offer a new 3DS challenge
      if (verifyResult.nextAction) {
        if (!actionResult.success) {
          console.log("3DS failed but cascade triggered new action, continuing loop...");
        } else {
          console.log("Another user action required, continuing loop...");
        }
        currentResult = verifyResult;
        continue;
      }

      if (!actionResult.success) {
        // 3DS failed and no cascade/retry available - return error
        // Include verifyResult as checkout_response so error_code is available to frontend
        console.log("3DS authentication failed, checkout concluded:", verifyResult);
        timingTracker.trackFail(
          verifyResult.checkoutAttemptId || null,
          verifyResult.errorCode || "3ds_failed",
          verifyResult.errorMessageForCustomer || actionResult.error,
        );
        return {
          errors: {
            root: verifyResult.errorMessageForCustomer || actionResult.error,
            checkout_response: verifyResult,
          },
        };
      }

      // 3DS auth passed AND no further action — but the charge may still have been declined.
      // Gate the success return on the checkout state so a pass-then-decline
      // (state=payment_failed) reports onError instead of a false onSuccess.
      if (isCheckoutFailure(verifyResult)) {
        console.log("Card checkout concluded in a failure state after 3DS:", verifyResult);
        timingTracker.trackFail(
          verifyResult.checkoutAttemptId || null,
          verifyResult.errorCode || "payment_failed",
          verifyResult.errorMessageForCustomer || "Payment failed",
        );
        return {
          errors: {
            root: verifyResult.errorMessageForCustomer || "Payment failed",
            checkout_response: verifyResult,
          },
        };
      }

      console.log("Card checkout verified ✅", verifyResult);

      // No more actions needed, return the result
      timingTracker.trackSuccess(verifyResult.checkoutAttemptId || "unknown");
      return { data: verifyResult as { [key: string]: unknown } };
    }

    if (userActionCount >= MAX_USER_ACTIONS) {
      console.error("Max user actions exceeded");
      timingTracker.trackFail(null, "max_actions_exceeded", "Too many authentication attempts");
      return { errors: { root: "Too many authentication attempts. Please try again." } };
    }

    // Check if checkout succeeded or failed
    // States: checkout_succeeded (success), payment_failed (failure)
    if (isCheckoutFailure(currentResult)) {
      console.log("Card checkout failed:", currentResult);
      timingTracker.trackFail(
        currentResult.checkoutAttemptId || null,
        currentResult.errorCode || "payment_failed",
        currentResult.errorMessageForCustomer || "Payment failed",
      );
      // Return the full response as errors so frontend can display error details
      // including error_code, error_message_for_customer, error_message_for_debug
      return {
        errors: {
          root: currentResult.errorMessageForCustomer || "Payment failed",
          checkout_response: currentResult,
        },
      };
    }

    console.log("Card checkout completed ✅", currentResult);
    timingTracker.trackSuccess(currentResult.checkoutAttemptId || "unknown");
    return { data: currentResult as { [key: string]: unknown } };
  };
  return submitPayment;
};

/**
 * Fetches a VGS CMP access token from the backend.
 * Uses AbortController with 10s timeout to prevent indefinite hangs on slow networks.
 */
const FETCH_COLLECT_TOKEN_TIMEOUT_MS = 10_000;

const fetchCollectToken = async (apiBaseUrl: string, secureToken: string): Promise<string> => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), FETCH_COLLECT_TOKEN_TIMEOUT_MS);

  try {
    const resp = await fetch(`${apiBaseUrl}/api/card-setup-intents/${secureToken}/collect-token`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      signal: controller.signal,
    });
    if (!resp.ok) {
      throw new Error(`Failed to get collect token: ${resp.status}`);
    }
    const data = await resp.json();
    return data.access_token;
  } finally {
    clearTimeout(timeoutId);
  }
};

/**
 * Creates a card via VGS CMP and sends aliases to our backend.
 *
 * Flow:
 * 1. Fetch CMP access token from our backend
 * 2. Call form.createCard() → VGS creates card, returns aliases
 * 3. Send aliases + fraud metadata to our backend via updateCardSetupIntent
 */
/**
 * Map VGS field-level error keys to PaymentKitErrors field keys.
 *
 * VGS CMP errorCallback uses short field names ("pan", "cvc", "exp-date")
 * matching the CMP field type, NOT the longer CSS-style names. Both forms
 * are mapped here for robustness.
 */
const VGS_FIELD_MAP: Record<string, keyof PaymentKitErrors> = {
  // Actual VGS CMP error keys (verified empirically)
  pan: "card_pan",
  cvc: "card_cvc",
  "exp-date": "card_exp",
  // Alternative key formats (VGS documentation references)
  "card-number": "card_pan",
  "card-security-code": "card_cvc",
  "card-expiration-date": "card_exp",
};

/**
 * Safely stringify an error for console logging.
 * VGS errors are plain objects that print as [object Object] without this.
 */
const stringifyError = (err: unknown): string => {
  if (err instanceof Error) return err.message;
  try {
    return JSON.stringify(err);
  } catch {
    return String(err);
  }
};

const submitVgsCardFields = async (
  states: CardStates,
  tunnelX: Awaited<ReturnType<typeof TunnelXManager.createFromPenpalConnection>>,
): Promise<{ errors: PaymentKitErrors; isSuccess: boolean }> => {
  const { vgsForm, cardSetupIntentId, secureToken, apiBaseUrl, checkoutRequestId } = states;
  const logPrefix = `[PaymentKit] [${checkoutRequestId}]`;
  const errors = {} as PaymentKitErrors;

  if (!vgsForm) {
    errors.card_pan = "required";
    return { errors, isSuccess: false };
  }

  if (!cardSetupIntentId) {
    errors.root = "Card setup intent not created";
    return { errors, isSuccess: false };
  }

  // Step 1: Get CMP access token from our backend
  let accessToken: string;
  const collectTokenStart = performance.now();
  try {
    accessToken = await fetchCollectToken(apiBaseUrl, secureToken);
    const collectTokenDuration = Math.round(performance.now() - collectTokenStart);
    AnalyticsService.capture("vgs_collect_token_timing", {
      checkout_session_id: secureToken,
      request_id: checkoutRequestId,
      duration_ms: collectTokenDuration,
      success: true,
    });
  } catch (err) {
    const collectTokenDuration = Math.round(performance.now() - collectTokenStart);
    const isAbortError = err instanceof DOMException && err.name === "AbortError";
    AnalyticsService.capture("vgs_collect_token_timing", {
      checkout_session_id: secureToken,
      request_id: checkoutRequestId,
      duration_ms: collectTokenDuration,
      success: false,
      error_type: isAbortError ? "FETCH_TIMEOUT" : "FETCH_ERROR",
    });
    console.error(`${logPrefix} VGS auth token fetch failed:`, stringifyError(err));
    errors.root = isAbortError
      ? "Card authentication timed out — please try again"
      : "Card authentication failed — please try again";
    return { errors, isSuccess: false };
  }

  // Step 2: Create card via VGS CMP — returns aliases for PAN/CVC, plain text expiry
  let cardResult: VGSCreateCardResponse;
  const VGS_CREATE_CARD_TIMEOUT_MS = 120_000;
  const createCardStart = performance.now();
  try {
    let createCardTimeoutId: ReturnType<typeof setTimeout>;
    cardResult = await Promise.race([
      new Promise<VGSCreateCardResponse>((resolve, reject) => {
        vgsForm.createCard(
          {
            auth: accessToken,
            data: { cardholder: {} },
          },
          (_status, cardObject) => resolve(cardObject),
          (error) => reject(error),
        );
      }),
      new Promise<never>((_, reject) => {
        createCardTimeoutId = setTimeout(() => reject(new Error("VGS_TIMEOUT")), VGS_CREATE_CARD_TIMEOUT_MS);
      }),
    ]).finally(() => clearTimeout(createCardTimeoutId));

    // Emit timing event on success
    const createCardDuration = Math.round(performance.now() - createCardStart);
    AnalyticsService.capture("vgs_create_card_timing", {
      checkout_session_id: secureToken,
      request_id: checkoutRequestId,
      duration_ms: createCardDuration,
      success: true,
    });
  } catch (err) {
    const createCardDuration = Math.round(performance.now() - createCardStart);
    const errStr = stringifyError(err);
    console.error(`${logPrefix} VGS createCard failed:`, errStr);

    // Distinguish timeout from VGS field validation errors
    if (err instanceof Error && err.message === "VGS_TIMEOUT") {
      AnalyticsService.capture("vgs_create_card_timing", {
        checkout_session_id: secureToken,
        request_id: checkoutRequestId,
        duration_ms: createCardDuration,
        success: false,
        error_type: "VGS_TIMEOUT",
      });
      errors.root = "Card processing timed out — please try again";
      return { errors, isSuccess: false };
    }

    // VGS errorCallback returns field-level errors as a plain object,
    // e.g. { "card-number": { errorMessages: ["Card number is not valid"] } }
    // Map them to per-field errors so inline validation shows on the right field.
    if (err && typeof err === "object" && !(err instanceof Error)) {
      const vgsErrors = err as Record<string, unknown>;
      for (const [vgsKey, fieldKey] of Object.entries(VGS_FIELD_MAP)) {
        if (vgsKey in vgsErrors) {
          const fieldErr = vgsErrors[vgsKey] as Record<string, unknown> | undefined;
          const messages = fieldErr?.errorMessages;
          errors[fieldKey] = Array.isArray(messages) ? messages[0] : "invalid";
        }
      }
      if (Object.keys(errors).length > 0) {
        AnalyticsService.capture("vgs_create_card_timing", {
          checkout_session_id: secureToken,
          request_id: checkoutRequestId,
          duration_ms: createCardDuration,
          success: false,
          error_type: "VGS_VALIDATION_ERROR",
        });
        return { errors, isSuccess: false };
      }
    }

    // Generic VGS error
    AnalyticsService.capture("vgs_create_card_timing", {
      checkout_session_id: secureToken,
      request_id: checkoutRequestId,
      duration_ms: createCardDuration,
      success: false,
      error_type: "VGS_ERROR",
    });
    errors.root = "Card validation failed — please check your card details and try again";
    return { errors, isSuccess: false };
  }

  // Step 3: Extract aliases and expiry from CMP response
  if (!cardResult?.data?.attributes) {
    console.error(`${logPrefix} VGS createCard returned invalid response:`, stringifyError(cardResult));
    errors.root = "Card processing returned an invalid response — please try again";
    return { errors, isSuccess: false };
  }
  const { pan_alias, cvc_alias, exp_month, exp_year } = cardResult.data.attributes;

  if (!cvc_alias) {
    console.warn(
      `${logPrefix} VGS createCard response missing cvc_alias (303 dedup):`,
      JSON.stringify(cardResult.data.attributes),
    );
  }

  const fullYear = exp_year < 100 ? 2000 + exp_year : exp_year;
  const formattedExp = `${String(exp_month).padStart(2, "0")}/${fullYear}`;

  // Capture CMP card ID for network token tracking
  const cmpCardId = cardResult.data.id;

  // Step 4: Send aliases + fraud metadata to our backend.
  // CVC is sent as a VGS alias from createCard. Additionally, the CVC is
  // submitted via the vault inbound proxy (REDACT route) as a fallback for
  // the 303 deduplication path where VGS returns an existing card without
  // a fresh cvc_alias.
  try {
    const updateIntentCall = tunnelX.publicEndpoints.updateCardSetupIntent({
      checkoutToken: secureToken,
      cardSetupIntentId: cardSetupIntentId,
      updateCardSetupIntentReq: {
        cardPan: pan_alias,
        cardExp: formattedExp,
        ...(cvc_alias ? { cardCvc: cvc_alias } : {}),
        cmpCardId: cmpCardId,
        fraudMetadata: collectFraudMetadata(),
      },
    });

    const proxyCvcCall = new Promise<void>((resolve, reject) => {
      if (!vgsForm.submit) {
        reject(new Error("VGS form.submit not available"));
        return;
      }
      vgsForm.submit(
        `/api/card-setup-intents/${secureToken}/${cardSetupIntentId}`,
        {
          method: "post",
          headers: { "Content-Type": "application/json" },
          data: (fields: Record<string, unknown>) => ({ card_cvc: fields.cvc }),
        },
        (_status: number) => resolve(),
        (err: unknown) => reject(err),
      );
    });

    await Promise.all([updateIntentCall, proxyCvcCall]);
  } catch (err) {
    console.error(`${logPrefix} updateCardSetupIntent failed:`, stringifyError(err));
    errors.root = "Failed to save card details — please try again";
    return { errors, isSuccess: false };
  }

  return { errors: {}, isSuccess: true };
};

const submitCardFields = async (states: CardStates) => {
  const errors = {} as PaymentKitErrors;
  const { cardSetupIntentId, cardInputConnections } = states;

  const submitPromises = Object.entries(cardInputConnections).map(async ([_type, connection]) => {
    const type = _type as PenpalCardInputType;

    const remote = await connection.promise;
    const result = await remote.onSubmit(cardSetupIntentId || "");

    if ("error" in result) {
      errors[type] = result.error;
    }
  });

  await Promise.allSettled(submitPromises);

  return {
    errors,
    isSuccess: Object.keys(errors).length === 0,
  };
};

const validateCardFields = async (states: CardStates) => {
  const errors: PaymentKitErrors = {};
  const { cardInputConnections } = states;

  const validatePromises = Object.entries(cardInputConnections).map(async ([_type, connection]) => {
    const type = _type as PenpalCardInputType;

    if (!connection) {
      errors[type] = "penpal_not_connected";
      return;
    }

    const remote = await connection.promise;
    const errorMsg = await remote.onValidate();

    if (errorMsg) {
      errors[type] = errorMsg;
    }
  });

  await Promise.allSettled(validatePromises);

  return {
    errors,
    isSuccess: Object.keys(errors).length === 0,
  };
};

const CardPaymentMethod = definePaymentMethod((paymentKitStates) => {
  // Object.create() creates prototype delegation: reads fall through to the shared
  // paymentKitStates (preserving auto-detected VGS config), writes stay local.
  // This prevents card-specific properties (cardInputConnections, vgsForm, etc.)
  // from leaking onto the shared paymentKitStates object.
  const localStates = Object.create(paymentKitStates) as CardStates;
  localStates.cardInputConnections = {};

  return {
    name: "card",
    externalFuncs: {
      createElement: defCreateElement(localStates),
    },
    internalFuncs: {
      submitPayment: defSubmitPayment(localStates),
      cleanup: () => {
        // Clean up VGS form if present
        if (localStates.vgsForm) {
          localStates.vgsFieldsCleanup?.();
          localStates.vgsFieldsCleanup = undefined;
          localStates.vgsForm.unmount();
          localStates.vgsForm = undefined;
        }
        // Clean up all card input iframe connections
        for (const connection of Object.values(localStates.cardInputConnections)) {
          connection?.destroy();
        }
        localStates.cardInputConnections = {};
      },
    },
  };
});

export default CardPaymentMethod;
